import { ref, onUnmounted, getCurrentInstance } from 'vue'
import { useImageDB, type ImageRecord, type UploadState } from '../utils/imageDb'
import {emitUploadEvent} from "../utils/uploadBus.ts";
import { deleteImageRecordsRemoteBulk, wasRemoteDeleted } from '../services/uploadService'

type UploadFn = (rec: ImageRecord) => Promise<{ remoteUrl: string; recordPatch?: Partial<ImageRecord> }>
type BatchUploadFn = (recs: ImageRecord[]) => Promise<Array<{ id: string; remoteUrl: string; recordPatch?: Partial<ImageRecord> }>>

const pendingCleanupDeleteIds = new Set<string>()
let cleanupDeleteTimer: ReturnType<typeof setTimeout> | null = null
const cancelledUploadIds = new Set<string>()
const STALE_UPLOAD_MS = 180_000

function queueRemoteCleanupDelete(id: string) {
    const wanted = String(id || '').trim()
    if (!wanted) return
    if (wasRemoteDeleted(wanted)) return
    pendingCleanupDeleteIds.add(wanted)
    if (cleanupDeleteTimer) return
    cleanupDeleteTimer = setTimeout(() => {
        const ids = [...pendingCleanupDeleteIds].filter((id) => !wasRemoteDeleted(id))
        pendingCleanupDeleteIds.clear()
        cleanupDeleteTimer = null
        if (!ids.length) return
        deleteImageRecordsRemoteBulk(ids).catch(() => {})
    }, 250)
}

export function useUploadRunner(
    uploadFn: UploadFn,
    opts?: { concurrency?: number; maxTries?: number; onUploaded?: (rec: ImageRecord) => void | Promise<void>; batchUploadFn?: BatchUploadFn; batchSize?: number; batchParallelJobs?: number },
) {
    const db = useImageDB('imageDB')
    const running = ref(false)
    const paused = ref(false)
    const inFlight = new Set<string>()
    let tickInProgress = false

    const concurrency = Math.max(1, opts?.concurrency ?? 1)
    const maxTries = Math.max(1, opts?.maxTries ?? 4)
    const batchSize = Math.max(1, Math.min(500, opts?.batchSize ?? concurrency))
    const batchParallelJobs = Math.max(1, opts?.batchParallelJobs ?? 1)

    function hasUploadSourceBlob(rec: ImageRecord | null | undefined) {
        return !!((rec as any)?.workingBlob || (rec as any)?.originalBlob)
    }

    async function quarantineNonUploadableQueueRecords() {
        const queued = await db.getByUploadState('queued').catch(() => [])
        const uploading = await db.getByUploadState('uploading').catch(() => [])
        const broken = [...queued, ...uploading].filter((rec) => {
            if (!rec?.id) return false
            if (String((rec as any)?.upload?.remoteUrl || '').trim()) return false
            return !hasUploadSourceBlob(rec)
        })
        if (!broken.length) return

        await Promise.allSettled(
            broken.map((rec) => updateUpload(rec.id, {
                state: 'failed',
                phase: 'failed',
                tries: maxTries,
                progressPct: 0,
                uploadedBytes: 0,
                totalBytes: Number((rec as any)?.bytes || 0) || 0,
                lastError: 'Lokale Uploaddaten fehlen. Bitte Bild neu hinzufügen.',
            })),
        )
    }

    async function recoverStuckUploads() {
        const uploading = await db.getByUploadState('uploading')
        const now = Date.now()
        await Promise.allSettled(
            uploading
                .filter((rec) => {
                    if (!rec?.id) return false
                    if (inFlight.has(rec.id)) return false
                    if (cancelledUploadIds.has(rec.id)) return false
                    // Datei ist hochgeladen, nur die Server-Verarbeitung läuft: nicht erneut hochladen.
                    if (rec?.upload?.phase === 'completing') return false
                    const updatedAt = Number(rec?.upload?.updatedAt || 0)
                    return updatedAt > 0 && (now - updatedAt) >= STALE_UPLOAD_MS
                })
                .map((rec) => updateUpload(rec.id, {
                    state: 'queued',
                    phase: 'queued',
                    progressPct: 0,
                    uploadedBytes: 0,
                    lastError: 'Upload wurde nach Timeout erneut eingeplant.',
                })),
        )
    }

    async function updateUpload(recId: string, patch: Partial<ImageRecord['upload']>) {
        const existing = await db.get(recId)
        if (!existing) return

        const nextUpload = {
            ...(existing.upload ?? { state: 'idle', tries: 0 }),
            ...patch,
            updatedAt: Date.now(),
        }

        await db.update(recId, {
            upload: nextUpload as any,
        } as any)

        emitUploadEvent({ type: 'upload:patched', id: recId, patch: { upload: nextUpload } })
    }
    async function pickNext(): Promise<ImageRecord | null> {
        // queued first
        const queued = await db.getByUploadState('queued')
        const nextQ = queued.find(r => !inFlight.has(r.id) && !cancelledUploadIds.has(r.id) && hasUploadSourceBlob(r))
        if (nextQ) return nextQ

        // retry failed if tries < maxTries
        const failed = await db.getByUploadState('failed')
        const nextF = failed.find(r => !inFlight.has(r.id) && !cancelledUploadIds.has(r.id) && hasUploadSourceBlob(r) && ((r.upload?.tries ?? 0) < maxTries))
        return nextF ?? null
    }

    async function pickNextBatch(limit: number): Promise<ImageRecord[]> {
        const out: ImageRecord[] = []
        const queued = await db.getByUploadState('queued')
        for (const rec of queued) {
            if (out.length >= limit) break
            if (inFlight.has(rec.id)) continue
            if (cancelledUploadIds.has(rec.id)) continue
            if (!hasUploadSourceBlob(rec)) continue
            out.push(rec)
        }

        if (out.length >= limit) return out

        const failed = await db.getByUploadState('failed')
        for (const rec of failed) {
            if (out.length >= limit) break
            if (inFlight.has(rec.id)) continue
            if (cancelledUploadIds.has(rec.id)) continue
            if (!hasUploadSourceBlob(rec)) continue
            if ((rec.upload?.tries ?? 0) >= maxTries) continue
            out.push(rec)
        }

        return out
    }

    async function runOne(rec: ImageRecord) {
        inFlight.add(rec.id)
        try {
            if (cancelledUploadIds.has(rec.id)) {
                // Stornierung verarbeitet -> id freigeben, damit eine wiederverwendete
                // id (auch instanzübergreifend) nicht dauerhaft blockiert bleibt.
                cancelledUploadIds.delete(rec.id)
                return
            }
            const tries = (rec.upload?.tries ?? 0) + 1
            await updateUpload(rec.id, {
                state: 'uploading',
                phase: 'uploading',
                tries,
                lastError: '',
                progressPct: 0,
                uploadedBytes: 0,
                totalBytes: Number((rec as any)?.bytes || 0) || 0,
            })

            const result = await uploadFn(rec)
            const latestAfterUpload = await db.get(rec.id)
            if (!latestAfterUpload) {
                queueRemoteCleanupDelete(rec.id)
                return
            }

            if (result.recordPatch) {
                await db.update(rec.id, result.recordPatch as any)
                emitUploadEvent({ type: 'upload:patched', id: rec.id, patch: result.recordPatch })
            }

            const shouldReleaseHeavyAfterUpload = !!((latestAfterUpload as any)?.editorBlob || (latestAfterUpload as any)?.thumbBlob)
            await db.update(rec.id, shouldReleaseHeavyAfterUpload
                ? ({
                    originalBlob: undefined,
                    workingBlob: undefined,
                } as any)
                : ({
                    originalBlob: undefined,
                } as any))

            await updateUpload(rec.id, {
                state: 'done',
                phase: 'done',
                remoteUrl: result.remoteUrl,
                lastError: '',
                progressPct: 100,
                uploadedBytes: Number((latestAfterUpload as any)?.bytes || (rec as any)?.bytes || 0) || 0,
                totalBytes: Number((latestAfterUpload as any)?.bytes || (rec as any)?.bytes || 0) || 0,
            })
            // Upload finalisiert -> evtl. vorgemerkte Stornierung dieser id verwerfen.
            cancelledUploadIds.delete(rec.id)
            try {
                const latest = await db.get(rec.id)
                void opts?.onUploaded?.({
                    ...(latest || rec),
                    ...(result.recordPatch || {}),
                    upload: {
                        ...(((latest || rec) as any)?.upload || rec.upload || { state: 'idle', tries: 0 }),
                        state: 'done',
                        remoteUrl: result.remoteUrl,
                        lastError: '',
                        updatedAt: Date.now(),
                    },
                    originalBlob: undefined,
                    ...(shouldReleaseHeavyAfterUpload ? { workingBlob: undefined } : {}),
                } as ImageRecord)
            } catch {}
        } catch (e: any) {
            const msg = e?.message || String(e)
            await updateUpload(rec.id, { state: 'failed', phase: 'failed', lastError: msg })
        } finally {
            inFlight.delete(rec.id)
        }
    }

    async function runBatch(records: ImageRecord[]) {
        const batch = (records || []).filter((rec) => {
            if (!rec) return false
            if (cancelledUploadIds.has(rec.id)) {
                // Stornierung verarbeitet -> id freigeben (siehe runOne).
                cancelledUploadIds.delete(rec.id)
                return false
            }
            return true
        })
        if (!batch.length) return

        for (const rec of batch) inFlight.add(rec.id)

        try {
            for (const rec of batch) {
                const tries = (rec.upload?.tries ?? 0) + 1
                await updateUpload(rec.id, {
                    state: 'uploading',
                    phase: 'uploading',
                    tries,
                    lastError: '',
                    progressPct: 0,
                    uploadedBytes: 0,
                    totalBytes: Number((rec as any)?.bytes || 0) || 0,
                })
            }

            const results = await opts?.batchUploadFn?.(batch)
            const byId = new Map<string, { id: string; remoteUrl: string; recordPatch?: Partial<ImageRecord>; processing?: boolean }>()
            for (const result of results || []) {
                const id = String(result?.id || '').trim()
                if (id) byId.set(id, result)
            }

            const settled = await Promise.allSettled(batch.map(async (rec) => {
                const result = byId.get(rec.id)
                if (result?.processing && !result.remoteUrl) {
                    // Hochgeladen, Server erzeugt noch Vorschauen. Kein Fehler: Editor/Designer
                    // fragen /upload/list ab und setzen den Datensatz auf "done", sobald fertig.
                    await updateUpload(rec.id, { state: 'uploading', phase: 'completing', progressPct: 100, lastError: '' })
                    cancelledUploadIds.delete(rec.id)
                    return
                }
                if (!result?.remoteUrl) {
                    throw new Error(`Batch upload result missing for image ${rec.id}`)
                }
                const latestAfterUpload = await db.get(rec.id)
                if (!latestAfterUpload) {
                    queueRemoteCleanupDelete(rec.id)
                    return
                }

                if (result.recordPatch) {
                    await db.update(rec.id, result.recordPatch as any)
                    emitUploadEvent({ type: 'upload:patched', id: rec.id, patch: result.recordPatch })
                }

                const shouldReleaseHeavyAfterUpload = !!((latestAfterUpload as any)?.editorBlob || (latestAfterUpload as any)?.thumbBlob)
                await db.update(rec.id, shouldReleaseHeavyAfterUpload
                    ? ({
                        originalBlob: undefined,
                        workingBlob: undefined,
                    } as any)
                    : ({
                        originalBlob: undefined,
                    } as any))

                await updateUpload(rec.id, {
                    state: 'done',
                    phase: 'done',
                    remoteUrl: result.remoteUrl,
                    lastError: '',
                    progressPct: 100,
                    uploadedBytes: Number((latestAfterUpload as any)?.bytes || (rec as any)?.bytes || 0) || 0,
                    totalBytes: Number((latestAfterUpload as any)?.bytes || (rec as any)?.bytes || 0) || 0,
                })
                // Upload finalisiert -> evtl. vorgemerkte Stornierung dieser id verwerfen.
                cancelledUploadIds.delete(rec.id)
                try {
                    const latest = await db.get(rec.id)
                    void opts?.onUploaded?.({
                        ...(latest || rec),
                        ...(result.recordPatch || {}),
                        upload: {
                            ...(((latest || rec) as any)?.upload || rec.upload || { state: 'idle', tries: 0 }),
                            state: 'done',
                            remoteUrl: result.remoteUrl,
                            lastError: '',
                            updatedAt: Date.now(),
                        },
                        originalBlob: undefined,
                        ...(shouldReleaseHeavyAfterUpload ? { workingBlob: undefined } : {}),
                    } as ImageRecord)
                } catch {}
            }))
            for (const r of settled) {
                if (r.status === 'rejected') console.warn('[upload][batch-finalize] failed', r.reason)
            }
        } catch (_e: any) {
            const failedIds = Array.isArray(_e?.failedIds)
                ? _e.failedIds.map((id: any) => String(id || '').trim()).filter(Boolean)
                : batch.map((rec) => String(rec?.id || '').trim()).filter(Boolean)
            const failedSet = new Set(failedIds)
            await Promise.allSettled(batch.filter((rec) => failedSet.has(String(rec?.id || '').trim())).map(async (rec) => {
                try {
                    await runOne(rec)
                } catch {}
            }))
        } finally {
            for (const rec of batch) inFlight.delete(rec.id)
        }
    }

    async function tick() {
        if (tickInProgress) return
        tickInProgress = true
        let didWork = false
        try {
        if (!running.value || paused.value) return
        await quarantineNonUploadableQueueRecords()
        await recoverStuckUploads()
        const slots = concurrency - inFlight.size
        if (slots <= 0) return

        if (opts?.batchUploadFn) {
            const jobs: Promise<void>[] = []
            let remainingJobs = batchParallelJobs

            while (remainingJobs > 0) {
                const batch = await pickNextBatch(batchSize)
                if (!batch.length) break
                jobs.push(runBatch(batch))
                remainingJobs -= 1
            }

            if (!jobs.length) return
            didWork = true
            await Promise.allSettled(jobs)
            return
        }

        const jobs: Promise<void>[] = []
        for (let i = 0; i < slots; i++) {
            const rec = await pickNext()
            if (!rec) break
            jobs.push(runOne(rec))
        }
        didWork = jobs.length > 0
        await Promise.allSettled(jobs)
        } finally {
            tickInProgress = false
            if (didWork && running.value && !paused.value) {
                queueMicrotask(() => {
                    tick().catch(() => {})
                })
            }
        }
    }

    let watchdogTimer: any = null

    function startAuto(_intervalMs = 1200) {
        if (running.value) return
        running.value = true
        tick().catch(() => {})
        watchdogTimer = setInterval(() => {
            if (!running.value || paused.value) return
            recoverStuckUploads()
                .then(() => tick())
                .catch(() => {})
        }, 15000)
    }

    function stopAuto() {
        running.value = false
        if (watchdogTimer) clearInterval(watchdogTimer)
        watchdogTimer = null
    }

    // Falls die besitzende Komponente unmountet ohne stopAuto() aufzurufen, würde der
    // setInterval-Watchdog leaken. getCurrentInstance()-Guard, da dieses Composable
    // auch außerhalb von setup() verwendet werden kann.
    if (getCurrentInstance()) {
        onUnmounted(() => { stopAuto() })
    }

    function pause() { paused.value = true }
    function resume() { paused.value = false; tick().catch(() => {}) }

    function cancel(ids: string[] | string) {
        const list = Array.isArray(ids) ? ids : [ids]
        for (const rawId of list) {
            const id = String(rawId || '').trim()
            if (!id) continue
            cancelledUploadIds.add(id)
        }
    }

    // ✅ Retry: setzt failed -> queued, tries bleibt (oder optional reset)
    async function retry(id: string, mode: 'keepTries' | 'resetTries' = 'keepTries') {
        const rec = await db.get(id)
        if (!rec) return
        if (inFlight.has(id)) return

        const tries = mode === 'resetTries' ? 0 : (rec.upload?.tries ?? 0)
        await updateUpload(id, { state: 'queued', phase: 'queued', tries, lastError: '', progressPct: 0, uploadedBytes: 0 })
        // direkt anstoßen
        tick().catch(() => {})
    }

    async function retryAllFailed() {
        const failed = await db.getByUploadState('failed')
        await Promise.all(
            failed
                .filter(r => !inFlight.has(r.id) && ((r.upload?.tries ?? 0) < maxTries))
                .map(r => updateUpload(r.id, { state: 'queued', phase: 'queued', lastError: '', progressPct: 0, uploadedBytes: 0 })),
        )
        tick().catch(() => {})
    }

    return {
        running,
        paused,
        cancel,
        startAuto,
        stopAuto,
        pause,
        resume,
        tick,
        retry,
        retryAllFailed,
    }
}
